<template>
  <section class="adjudication-hero">
    <div class="adjudication-hero__card">
      <div class="adjudication-hero__content">
        <DsChip label="Tu progreso cuenta" variant="orange" />

        <div class="adjudication-hero__title">
          <DsTextSplit
            as="h3"
            base="Est&aacute;s a un paso"
            highlight="de recibir tu financiamiento"
            baseColor="var(--ds-purple-400)"
            highlightColor="var(--ds-purple-900)"
          />
        </div>

        <DsText as="body4" class="adjudication-hero__intro">
          Conoce c&oacute;mo funciona la adjudicaci&oacute;n y qu&eacute; necesitas para avanzar sin contratiempos.
        </DsText>

        <div class="adjudication-hero__section">
          <DsText as="bebas4" color="var(--ds-purple-900)">
            &iquest;Qu&eacute; es la adjudicaci&oacute;n?
          </DsText>
          <DsText as="body3" class="adjudication-hero__description">
            Tu camino est&aacute; dividido en etapas claras. Aqu&iacute; puedes entender en cu&aacute;l est&aacute;s y qu&eacute; sigue:
          </DsText>
        </div>

        <div class="adjudication-hero__bullets">
          <DsIcon variant="bullet" :iconSrc="adjudicacionPeopleIcon" text="Es el proceso donde se asigna qui&eacute;n recibe el financiamiento" />
          <DsIcon variant="bullet" :iconSrc="adjudicacionHouseIcon" text="Participan clientes al corriente en pagos" />
          <DsIcon variant="bullet" :iconSrc="adjudicacionPaperIcon" text="Se basa en reglas claras del contrato" />
        </div>

        <div class="adjudication-hero__actions">
          <DsButton
            label="Entender la adjudicación"
            :right-icon-src="arrowWhiteIcon"
            class="adjudication-hero__button"
            @click="irAAdjudicacion"
          />
          <DsButton
            label="Ver mi estatus"
            variant="outlined"
            :right-icon-src="arrowPurpleIcon"
            class="adjudication-hero__button"
            @click="irAEstatus"
          />
        </div>
      </div>

      <div class="adjudication-hero__image-wrap">
        <picture>
          <source media="(max-width: 767px)" :srcset="adjudicacionHeroMobile" />
          <source media="(max-width: 1023px)" :srcset="adjudicacionHeroTablet" />
          <img
            :src="adjudicacionHeroDesktop"
            alt="Pareja celebrando su nuevo hogar"
            class="adjudication-hero__image"
          />
        </picture>
      </div>
    </div>

    <DsText as="h3" class="adjudication-hero__quote">
      "Tu constancia<br class="adjudication-hero__quote-break" />
      es lo que te acerca a<br class="adjudication-hero__quote-break" />
      este momento"
    </DsText>
  </section>
</template>

<script setup lang="ts">
import DsButton from '../atoms/DsButtonIcon.vue'
import DsChip from '../atoms/DsChip.vue'
import DsIcon from '../atoms/DsIcon.vue'
import DsText from '../atoms/DsText.vue'
import DsTextSplit from '../atoms/DsTextSplit.vue'

const adjudicacionHouseIcon = new URL('../../../../../assets/adjudicacion/adjudicaciones_house_orange.svg', import.meta.url).href
const adjudicacionPaperIcon = new URL('../../../../../assets/adjudicacion/adjudicaciones_paper_orange.svg', import.meta.url).href
const adjudicacionPeopleIcon = new URL('../../../../../assets/adjudicacion/adjudicaciones_people_orange.svg', import.meta.url).href
const adjudicacionHeroDesktop = new URL('../../../../../assets/adjudicacion/adjudicaciones_desktop.png', import.meta.url).href
const adjudicacionHeroMobile = new URL('../../../../../assets/adjudicacion/adjudicaciones_mobile.png', import.meta.url).href
const adjudicacionHeroTablet = new URL('../../../../../assets/adjudicacion/adjudicaciones_tablet.png', import.meta.url).href
const arrowWhiteIcon = new URL('../../../../../assets/adjudicacion/adjudicaciones_arrow.svg', import.meta.url).href
const arrowPurpleIcon = new URL('../../../../../assets/adjudicacion/adjudicaciones_arrow_purple.svg', import.meta.url).href

/*
  TODO: destino pendiente de definir con producto. Por ahora ambos botones
  permanecen en la propia página (sin navegación).
*/
function irAAdjudicacion() {
  // Pendiente: destino por confirmar.
}

function irAEstatus() {
  // Pendiente: destino por confirmar.
}
</script>

<style scoped>
.adjudication-hero {
  max-width: 962px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.adjudication-hero__card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.adjudication-hero__content {
  background: var(--ds-grey-50);
  border-radius: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 28px 24px;
}

.adjudication-hero__bullets {
  display: grid;
  gap: 32px;
}

/*
  El Figma muestra los íconos naranjas sueltos, sin el círculo morado que
  DsIcon aplica en su variante "bullet". Se neutraliza aquí para no alterar
  el átomo, que otras secciones usan con el círculo.
*/
.adjudication-hero__bullets :deep(.ds-icon__circle) {
  background-color: transparent;
  border-radius: 0;
}

.adjudication-hero__bullets :deep(.ds-icon__image) {
  width: 32px;
  height: 32px;
}

.adjudication-hero__actions {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.adjudication-hero__button {
  width: 100%;
}

.adjudication-hero__title :deep(.ds-text-split) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.adjudication-hero__intro,
.adjudication-hero__description {
  color: var(--ds-text-primary);
  line-height: 1.6;
  font-size: var(--ds-body3-size);
}

.adjudication-hero__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.adjudication-hero__image-wrap {
  overflow: hidden;
  border-radius: 20px;
  min-height: 196px;
}

.adjudication-hero__image-wrap picture {
  display: block;
  width: 100%;
  height: 100%;
}

.adjudication-hero__image {
  width: 100%;
  height: 100%;
  min-height: 196px;
  display: block;
  object-fit: cover;
  object-position: center;
}

.adjudication-hero__quote {
  color: var(--ds-purple-900);
  text-align: center;
  line-height: 1.45;
  font-size: 24px;
  font-weight: 400;
}

.adjudication-hero__quote-break {
  display: none;
}

@media (max-width: 767px) {
  .adjudication-hero__quote-break {
    display: block;
  }
}

/* 744px: ancho del diseño de tablet (Figma node 795:5999). */
@media (min-width: 744px) {
  .adjudication-hero {
    gap: 36px;
  }

  .adjudication-hero__card {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    background: var(--ds-grey-50);
    border-radius: 24px;
    overflow: hidden;
  }

  .adjudication-hero__content {
    border-radius: 0;
    justify-content: center;
    gap: 16px;
    padding: 30px 26px;
  }

  .adjudication-hero__image-wrap {
    border-radius: 0 24px 24px 0;
    min-height: 415px;
  }

  .adjudication-hero__image {
    min-height: 415px;
  }
}

@media (min-width: 1024px) {
  .adjudication-hero {
    margin: 0 auto;
  }

  .adjudication-hero__card {
    border-radius: 0;
  }

  .adjudication-hero__content {
    gap: 18px;
    padding: 36px 32px;
  }

  .adjudication-hero__image-wrap,
  .adjudication-hero__image {
    min-height: 384px;
  }
}
</style>

